import { z } from 'zod'
import { DeathData } from '../deathData/deathData'
import { deathDataSchema } from '../deathData/deathDataSchemas'
import { BillingFrequency, ServiceType } from '../offerings/offering'
import { BasicFilters } from '..'
import { DocType } from '../shared'
import { PaymentPlanStatus } from './payment-plan'
import { SellCode } from '../parcels/parcel'
import { AssuredPersonRequest } from '../assured/Assured'

export enum AgreementStatus {
  Pending = 'pending',
  Active = 'active',
  Suspended = 'suspended',
  Terminated = 'terminated',
  Expired = 'expired',
}

export enum AgreementType {
  OneTime = 'one_time',
  Recurring = 'recurring'
}

export interface AgreementDocument {
  id: string
  agreementId: string
  fileName: string
  fileUrl: string
  fileSize: number
  mimeType: string
  uploadedAt: string
  uploadedBy: string
}

export interface Agreement {
  id: string
  contractNumber: string
  userId: string
  userName: string
  userCode: string
  offeringId: string
  amount: number
  offeringName: string
  billingFrequency: BillingFrequency
  status: AgreementStatus
  startDate: Date
  endDate?: Date
  notes?: string
  outstandingAmount?: number;
  mainContractId?: string;
  sellCode?: SellCode;
  collectorId?: string;
  sellerId?: string;
  updatedAt?: Date;
}

export interface SubscriptionData {
  id: string
  contractNumber: string
  offeringCode: string
  offeringName: string
  userName: string
  userDocType: DocType
  userDocNumber: string
  status: AgreementStatus
  startDate: Date
  endDate?: Date
  lastPeriodTotalAmount: number
  totalOutstandingAmount: number
  totalPeopleCovered: number
  updatedAt?: Date
}

export interface CremationData {
  id: string
  contractNumber: string
  offeringCode: string
  offeringName: string
  userName: string
  userDocType: DocType
  userDocNumber: string
  status: AgreementStatus
  startDate: Date
  endDate?: Date
  deceasedName: string
  deceasedDeathDate: Date
  sellCode?: SellCode
  totalAmount: number
  totalPaidAmount: number
  totalOutstandingAmount: number
  updatedAt?: Date
}

export interface PaymentPlanData {
  id: string
  contractNumber: string
  userName: string
  userDocType: DocType
  userDocNumber: string
  offeringName: string
  status: PaymentPlanStatus
  initialPeriodDate: Date
  finalPeriodDate: Date
  initialAmount: number
  installmentAmount: number
  installmentValue: number
  totalAmount: number
  installments: number
  installmentsPaid: number
  totalPaidAmount: number
  totalOutstandingAmount: number
}

export interface CreateAgreementData {
  userId: string;
  offeringId: string;
  contractNumber?: string;
  serviceType?: ServiceType;
  notes?: string;
  status?: AgreementStatus;
  startDate: Date;
  endDate?: Date;
  amount: number;
  deathData?: DeathData[];
  assuredPeople?: AssuredPersonRequest[];
  hasPaymentPlan?: boolean;
  initialAmount?: number;
  installmentAmount?: number;
  installments?: number;
  parcelId?: string;
  mainContractId?: string;
  sellCode?: SellCode;
  sellerId?: string;
  collectorId?: string;
  updatedAt?: Date;
}

export interface UpdateAgreementData {
  notes?: string;
  sellerId?: string | null;
  collectorId?: string | null;
  sellCode?: string;
  expensesCode?: string;
  amount?: number;
}

export interface AgreementFilters {
  clientId?: string
  offeringId?: string
  status?: AgreementStatus
  serviceType?: string
  search?: string
  dateFrom?: string
  dateTo?: string
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: string
}

export type SubscriptionFilters = BasicFilters & {
  status?: string
  offeringId?: string
  serviceType?: string
  dateFrom?: string
  dateTo?: string
  userId?: string
}

export type CremationFilters = BasicFilters & {
  status?: string;
  startDate?: string;
  endDate?: string;
  userId?: string;
}

const _baseSchemaFields = (serviceType: string) => {
  const prefixMap: Record<string, string> = {
    [ServiceType.ABONOS]: 'A',
    [ServiceType.PARCELAS]: 'P',
    [ServiceType.CREMACIONES]: 'C',
  }

  const expectedPrefix = prefixMap[serviceType]
  const contractNumberValidation = expectedPrefix
    ? z.string().refine(
      (val) => !val || new RegExp(`^${expectedPrefix}-\\d+$`).test(val),
      { message: `El número de contrato debe tener el formato ${expectedPrefix}-XXX` }
    )
    : z.any()

  return {
    userId: z.string().min(1, 'El cliente es requerido'),
    offeringId: z.string().min(1, 'El servicio es requerido'),
    sellerId: z.string().optional(),
    collectorId: z.string().optional(),
    notes: z.string().optional(),
    startDate: z.date({ message: 'La fecha de inicio es requerida' }),
    endDate: z.date().optional(),
    amount: z.number().positive('El monto debe ser positivo'),
    hasPaymentPlan: z.boolean().optional(),
    deathData: z.array(deathDataSchema(serviceType)).optional(),
    initialAmount: z.number().optional(),
    installmentAmount: z.number().optional(),
    installments: z.number().optional(),
    parcelId: z.string().optional(),
    sellCode: z.enum(SellCode).optional(),
    contractNumber: contractNumberValidation.optional(),
  }
}

/** Schema for CREMACIONES agreements */
export const createCremacionesAgreementSchema = () =>
  z.object({
    ..._baseSchemaFields(ServiceType.CREMACIONES),
    endDate: z.date({ message: 'La fecha de cremación es requerida' }),
    sellCode: z.enum(SellCode, { error: 'El tipo de venta es requerido' }),
  })

/** Schema for ABONOS agreements (with or without a payment plan) */
export const createAbonosAgreementSchema = (hasPaymentPlan: boolean) =>
  z.object({
    ..._baseSchemaFields(ServiceType.ABONOS),
    ...(hasPaymentPlan
      ? {
        installmentAmount: z
          .number({ message: 'Debes ingresar un monto de anticipo para el servicio seleccionado' })
          .positive('El monto de la cuota debe ser positivo'),
        installments: z
          .number({ message: 'Debes elegir un número de cuotas' })
          .positive('Debes elegir un número de cuotas')
          .int(),
      }
      : {}),
  })

/** Schema for all other service types (e.g. generic / non-typed offerings) */
export const createBaseAgreementSchema = (serviceType: string) =>
  z.object({
    ..._baseSchemaFields(serviceType),
  })

export type AgreementSchema =
  | ReturnType<typeof createCremacionesAgreementSchema>
  | ReturnType<typeof createAbonosAgreementSchema>
  | ReturnType<typeof createBaseAgreementSchema>

export const formatAgreementStatus = (status: AgreementStatus): string => {
  const statuses: Record<AgreementStatus, string> = {
    [AgreementStatus.Pending]: 'Pendiente',
    [AgreementStatus.Active]: 'Activo',
    [AgreementStatus.Suspended]: 'Suspendido',
    [AgreementStatus.Terminated]: 'Completado',
    [AgreementStatus.Expired]: 'Expirado',
  }
  return statuses[status] || status
}

export const formatAgreementType = (type: AgreementType): string => {
  const types: Record<AgreementType, string> = {
    [AgreementType.OneTime]: 'Único',
    [AgreementType.Recurring]: 'Recurrente',
  }
  return types[type] || type
}

export const getAgreementStatusColor = (status: AgreementStatus): string => {
  switch (status) {
    case AgreementStatus.Pending:
      return 'bg-yellow-300 text-yellow-800 hover:bg-yellow-300'
    case AgreementStatus.Active:
      return 'bg-green-100 text-green-800 hover:bg-green-100'
    case AgreementStatus.Suspended:
      return 'bg-red-100 text-red-800 hover:bg-red-100'
    case AgreementStatus.Terminated:
      return 'bg-blue-200 text-blue-800 hover:bg-blue-200'
    case AgreementStatus.Expired:
      return 'bg-red-100 text-orange-800 hover:bg-red-100'
    default:
      return 'bg-gray-100 text-gray-800 hover:bg-gray-100'
  }
}

export const getPaymentPlanStatusColor = (status: PaymentPlanStatus): string => {
  switch (status) {
    case PaymentPlanStatus.CREATED:
      return 'bg-yellow-300 text-yellow-800 hover:bg-yellow-300'
    case PaymentPlanStatus.ACTIVE:
      return 'bg-green-100 text-green-800 hover:bg-green-100'
    case PaymentPlanStatus.PAYED:
      return 'bg-blue-200 text-blue-800 hover:bg-blue-200'
    case PaymentPlanStatus.CANCELLED:
      return 'bg-red-100 text-red-800 hover:bg-red-100'
    default:
      return 'bg-gray-100 text-gray-800 hover:bg-gray-100'
  }
}

export const formatPaymentPlanStatus = (status: PaymentPlanStatus): string => {
  const statuses: Record<PaymentPlanStatus, string> = {
    [PaymentPlanStatus.CREATED]: 'Creado',
    [PaymentPlanStatus.ACTIVE]: 'Activo',
    [PaymentPlanStatus.PAYED]: 'Pagado',
    [PaymentPlanStatus.CANCELLED]: 'Cancelado',
  }
  return statuses[status] || status
}

export const isAgreementActive = (agreement: Agreement): boolean => {
  return agreement.status === AgreementStatus.Active
}

export const isAgreementEditable = (agreement: Agreement): boolean => {
  return [AgreementStatus.Pending, AgreementStatus.Active].includes(agreement.status)
}
